import { FirebaseError } from "firebase/app";
import { ContentApiErrorResponse } from "@/types.ts";

class MissingClaim extends Error {
  constructor(claim: string) {
    super(`${claim} is missing from JWT`);
    this.name = 'MissingClaim';
  }
}

class SignInFailureError extends Error {
  constructor(message?: string) {
    super(message ?? "Something went wrong on our end. Please try again later. If the error persists, please contact support.");
    this.name = 'SignInFailureError';
  }
}

class MissingSessionError extends SignInFailureError {
  constructor(message?: string) {
    super(
      message ?? "This sign-in link is not valid for this device. Please access the link sent to your email address on the same device it was requested on, or request a new link and try again."
    );
    this.name = 'MissingSessionError';
  }
}

class InvalidLinkError extends SignInFailureError {
  constructor(message?: string) {
    super(message ?? "This sign-in link is invalid. Please request a new one and try again.");
    this.name = 'InvalidOrExpiredLinkError';
  }
}

class InvalidOrExpiredOrUsedLinkError extends SignInFailureError {
  constructor(message?: string) {
    super(message ?? "This sign-in link is expired or invalid. It may have already been used. Please request a new one and try again.");
    this.name = 'InvalidOrExpiredLinkError';
  }
}

class ExpiredLinkError extends SignInFailureError {
  constructor(message?: string) {
    super(message ?? "This sign-in link is expired. Please request a new one and try again.");
    this.name = 'ExpiredLinkError';
  }
}

class PermissionDeniedError extends SignInFailureError {
  constructor(message?: string) {
    super(message ?? "You are not permitted to view this resource");
    this.name = 'PermissionDeniedError';
  }
}

class FirebasePermissionDeniedError extends FirebaseError {}

class FetchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FetchError";
  }
}

class NetworkError extends FetchError {
  constructor(message?: string) {
    super(message ?? "A network error occurred. Please check your network connection and try again.");
    this.name = 'NetworkError';
  }
}

class TooManyRequestsError extends FetchError {
  constructor(message?: string) {
    super(message ?? "Too many requests. Please wait a while before trying again.");
    this.name = 'TooManyRequestsError';
  }
}

class UnknownError extends FetchError {
  constructor(message?: string) {
    super(
      `An unknown error occurred communicating with the server${
        message ? ": " + message : ""
      }`
    );
    this.name = "UnknownError";
  }
}

class ResponseError extends FetchError {
  status?: number;
  constructor(message?: string) {
    super(message ?? "A response error occurred");
    this.name = "ResponseError";
  }
}

class AllAttemptsFailedError extends FetchError {
  // Errors can unfortunately have any type (yay JS)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: Array<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(url: string, errors: Array<any>) {
    super(`All attempts failed to access ${url}`);
    this.errors = errors;
    this.name = "AllAttemptsFailedError";
  }

  getSingularError(){
    if(this.errors.length === 1 ){
      return this.errors[0];
    }
  }
}

// Extending ResponseError
class ContentApiJsonError extends ResponseError {
  code: string;
  constructor(status: number, json: ContentApiErrorResponse) {
    super(json.error.message);
    this.code = json.error.code;
    this.status = status;
    this.name = "ContentApiJsonError";
  }
}

class FTSpecialAccessApiError extends ResponseError {
  url: string;
  code: string;
  constructor(status: number, json: ContentApiErrorResponse) {
    super("Unlicensed - FT Licensing rules apply");
    this.code = json.error.code;
    this.url = json.error.message;
    this.status = status;
    this.name = "FTSpecialAccessApiError";
  }
}

class UnauthorisedError extends ResponseError {
  constructor(message?: string) {
    const baseMessage = "Unauthorised";
    const fullMessage = message ? `${baseMessage} - ${message}` : baseMessage;
    super(fullMessage);
    this.status = 401;
    this.name = "UnauthorisedError";
  }
}

class ForbiddenError extends ResponseError {
  constructor() {
    super("Forbidden");
    this.status = 403;
    this.name = "ForbiddenError";
  }
}

class NotFoundError extends ResponseError {
  constructor(message: string = "Not found") {
    super(message);
    this.status = 404;
    this.name = "NotFoundError";
  }
}

class InternalServerError extends ResponseError {
  constructor() {
    super("Something went wrong on our end. Please try again later.");
    this.status = 500;
    this.name = "InternalServerError";
  }
}

class BadGatewayError extends ResponseError {
  constructor() {
    super("Bad Gateway, try again later");
    this.status = 502;
    this.name = "BadGatewayError";
  }
}

class ServiceUnavailableError extends ResponseError {
  constructor() {
    super("Service Temporarily Unavailable, try again later");
    this.status = 503;
    this.name = "ServiceUnavailableError";
  }
}

class GatewayTimeoutError extends ResponseError {
  constructor() {
    super("Gateway Timeout, try again later");
    this.status = 504;
    this.name = "GatewayTimeoutError";
  }
}

class RedirectRulesNotFoundError extends NotFoundError {
  constructor() {
    super("Redirects are not enabled for this organisation");
    this.name = "RedirectRulesNotFoundError";
  }
}

class ArticleFetchError extends Error {
  constructor(m = 'An unknown error occurred when fetching article.') {
    super(m);
    this.name = 'ArticleFetchError';
  }
}

class ArticleInvalidRequestError extends ArticleFetchError {
  constructor(m = 'An unknown error occurred when fetching article.') {
    super(m);
    this.name = 'InvalidError';
  }
}

class InvalidArticleIdError extends ArticleFetchError {
  constructor(m = 'The request URL or query string is improperly formatted.') {
    super(m);
    this.name = 'InvalidArticleIdError';
  }
}

class ArticleNotFoundError extends ArticleFetchError {
  constructor(m = 'Article not found.') {
    super(m);
    this.name = 'ArticleNotFoundError';
  }
}

class IncorrectArticleIdSeparatorError extends ArticleFetchError {
  constructor(m = "Unrecognised value for the 'ids' parameter. Use commas to separate multiple article IDs. Example: 'ids=1234,5678'.") {
    super(m);
    this.name = 'IncorrectArticleIdSeparatorError';
  }
}

class TooManyArticleIdsError extends ArticleFetchError {
  constructor(m = "'ids' parameter accepts a maximum of 100 items.") {
    super(m);
    this.name = 'TooManyArticleIdsError';
  }
}

class MissingArticleIdParameterError extends ArticleFetchError {
  constructor(m = "The 'ids' parameter must contain at least one valid article ID.") {
    super(m);
    this.name = 'MissingArticleIdParameterError';
  }
}

class BranderMissingReplacementParamError extends ArticleFetchError {
  constructor(m = 'One or more template parameters are missing.') {
    super(m);
    this.name = 'BranderMissingReplacementParamError';
  }
}

export {
  MissingClaim,
  SignInFailureError,
  MissingSessionError,
  InvalidLinkError,
  ExpiredLinkError,
  InvalidOrExpiredOrUsedLinkError,
  PermissionDeniedError,
  NetworkError,
  TooManyRequestsError,
  FirebasePermissionDeniedError,
  FetchError,
  UnknownError,
  ResponseError,
  AllAttemptsFailedError,
  ContentApiJsonError,
  FTSpecialAccessApiError,
  UnauthorisedError,
  ForbiddenError,
  NotFoundError,
  InternalServerError,
  BadGatewayError,
  ServiceUnavailableError,
  GatewayTimeoutError,
  RedirectRulesNotFoundError,
  ArticleFetchError,
  ArticleInvalidRequestError,
  InvalidArticleIdError,
  ArticleNotFoundError,
  IncorrectArticleIdSeparatorError,
  TooManyArticleIdsError,
  MissingArticleIdParameterError,
  BranderMissingReplacementParamError,
};
